import { useSyncExternalStore } from "react";
import { api } from "./api";

const SEND_EVERY_MS = 15000;
const SEND_MOVE_M = 25;
const HEARTBEAT_MS = 60000;

let state = { status: "off", lastSentAt: null, error: null };
const listeners = new Set();
let watchId = null;
let heartbeat = null;
let lastPos = null;
let lastSent = null;
let inflight = false;

const set = (patch) => {
  state = { ...state, ...patch };
  listeners.forEach((l) => l());
};

const distM = (a, b) => {
  const r = 6371000, p1 = (a.lat * Math.PI) / 180, p2 = (b.lat * Math.PI) / 180;
  const dp = ((b.lat - a.lat) * Math.PI) / 180, dl = ((b.lng - a.lng) * Math.PI) / 180;
  const h = Math.sin(dp / 2) ** 2 + Math.cos(p1) * Math.cos(p2) * Math.sin(dl / 2) ** 2;
  return 2 * r * Math.asin(Math.sqrt(h));
};

async function send(pos) {
  if (inflight) return;
  inflight = true;
  try {
    await api.post("/tracking/ping", pos);
    lastSent = { ...pos, at: Date.now() };
    set({ status: "active", lastSentAt: lastSent.at, error: null });
  } catch (e) {
    set({ error: "Nie udało się wysłać pozycji" });
  } finally {
    inflight = false;
  }
}

function onPosition(p) {
  const c = p.coords;
  lastPos = {
    lat: c.latitude, lng: c.longitude, accuracy: c.accuracy ?? null,
    speed_kmh: c.speed == null || c.speed < 0 ? null : Math.round(c.speed * 3.6 * 10) / 10,
    heading: c.heading == null || Number.isNaN(c.heading) ? null : c.heading,
    timestamp: new Date(p.timestamp || Date.now()).toISOString(),
  };
  if (state.status !== "active") set({ status: "waiting" });
  const due = !lastSent || Date.now() - lastSent.at >= SEND_EVERY_MS || distM(lastSent, lastPos) >= SEND_MOVE_M;
  if (due) send(lastPos);
}

function onError(err) {
  if (err?.code === 1) set({ status: "denied", error: "Brak zgody na lokalizację" });
  else set({ error: err?.message || "Błąd lokalizacji" });
}

function startWatch() {
  if (watchId != null) navigator.geolocation.clearWatch(watchId);
  watchId = navigator.geolocation.watchPosition(onPosition, onError, {
    enableHighAccuracy: true, maximumAge: 5000, timeout: 20000,
  });
}

function onVisibility() {
  if (document.visibilityState === "visible" && watchId != null) startWatch();
}

export function startTracking() {
  if (state.status !== "off") return;
  if (typeof navigator === "undefined" || !navigator.geolocation) {
    set({ status: "unsupported", error: "Telefon nie obsługuje lokalizacji" });
    return;
  }
  set({ status: "waiting", error: null });
  startWatch();
  document.addEventListener("visibilitychange", onVisibility);
  heartbeat = setInterval(() => {
    if (lastPos && (!lastSent || Date.now() - lastSent.at >= HEARTBEAT_MS)) send({ ...lastPos, timestamp: new Date().toISOString() });
  }, HEARTBEAT_MS / 2);
}

export function stopTracking() {
  if (watchId != null && navigator.geolocation) navigator.geolocation.clearWatch(watchId);
  watchId = null;
  clearInterval(heartbeat);
  heartbeat = null;
  document.removeEventListener("visibilitychange", onVisibility);
  lastPos = null;
  lastSent = null;
  set({ status: "off", lastSentAt: null, error: null });
}

export const useTracking = () =>
  useSyncExternalStore((l) => { listeners.add(l); return () => listeners.delete(l); }, () => state);
